import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import { Container, Tab, Tabs, Box } from "@mui/material";
import { useState, useEffect } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import ButtonBackPage from "@components/ButtonBackPage";
import AccountingCost from "./AccountingCost";
import AccountingGoodReceipt from "./AccountingGoodReceipt";
import { useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";

interface TabLabelWithCountProps {
  label: string;
}

const TabLabelWithCount: React.FC<TabLabelWithCountProps> = ({ label }) => {
  const theme = useTheme();
  return (
    <div style={{ display: "inline-flex", alignItems: "center" }}>{label}</div>
  );
};

const Accounting: React.FC = () => {
  const { t } = useTranslation(["accounting-page"]);
  const [selectedTab, setSelectedTab] = useState(0);
  const [odaSapCode, setOdaSapCode] = useState<string | undefined>();
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const tab = searchParams.get("tab");

  const tabMap: Record<string, number> = {
    RDA: 0,
    BC_BE: 1,
    GR: 2,
  };

  useEffect(() => {
    if (!tab) return;

    const tabIndex = tabMap[tab];

    if (tabIndex !== undefined) {
      setSelectedTab(tabIndex);
    }
  }, [tab]);

  const handleTabChange = (newValue: number, odaSapCode?: string) => {
    setSelectedTab(newValue);
    setOdaSapCode(odaSapCode);
  };

  const tabs = [
    {
      id: "RDA",
      label: <TabLabelWithCount label={t("tab.rda")} />,
      component: (
        <AccountingCost accounting={"RDA"} handleTabChange={handleTabChange} />
      ),
    },
    {
      id: "BC_BE",
      label: <TabLabelWithCount label={t("tab.bc_be")} />,
      component: (
        <AccountingCost
          accounting={"BC_BE"}
          handleTabChange={handleTabChange}
        />
      ),
    },
    {
      id: "GR",
      label: <TabLabelWithCount label={t("tab.goods-receipts")} />,
      component: <AccountingGoodReceipt odaSapCode={odaSapCode} />,
    },
  ];

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-accounting.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <ButtonBackPage />
        <Box>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "#696969",
              zIndex: 2000,
              background: theme.palette.text.secondary,
            }}
          >
            <Tabs
              value={selectedTab}
              aria-label="table tabs"
              sx={{
                "& .MuiTab-root": {
                  color: theme.palette.text.primary,
                  textTransform: "none",
                  fontSize: "1.5rem",
                  position: "relative",
                  "&.Mui-selected::after": {
                    content: '""',
                    display: "block",
                    width: "100%",
                    height: "3px",
                    backgroundColor: theme.palette.accent.main,
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                  },
                },
                "& .MuiTab-root.Mui-selected": {
                  color: theme.palette.accent.main,
                },
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.id}
                  label={tab.label}
                  onClick={() => handleTabChange(index)}
                />
              ))}
            </Tabs>
          </Box>
          <Box>{tabs[selectedTab]?.component}</Box>
        </Box>
      </Container>
    </AnimationFadeIn>
  );
};

export default Accounting;
